import AIJob from 'models/AIJob';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { queryClient } from 'services/http';
import { type AIJobNotificationContext, AIJobNotificationType } from 'stores/aiJobNotificationStore';

import { simpleDateAtTime } from './date';

/**
 * Generates the notification title based on job context and state
 */
export const getAIJobNotificationTitle = (
  context: AIJobNotificationContext,
  state: 'processing' | 'completed' | 'failed'
): string => {
  const description = getJobDescription(context);

  switch (state) {
    case 'processing':
      return `Currently processing ${description}`;
    case 'completed':
      return `Processing complete for ${description}`;
    case 'failed':
      return `Processing failed for ${description}`;
  }
};

/**
 * Gets the job description for the notification title
 */
const getJobDescription = (context: AIJobNotificationContext): string => {
  const { jobType, summaryType, patientName, createdAt } = context;

  switch (jobType) {
    case AIJobNotificationType.AUTHORIZATION_SUMMARY:
      return `${patientName} - Concurrent Review Summary`;

    case AIJobNotificationType.AUTHORIZATION_COMPARISON:
      return `${patientName} - Concurrent Review Comparison`;

    case AIJobNotificationType.REFERRAL_SUMMARY:
      return `${patientName} - Referral Summary`;

    case AIJobNotificationType.WORKSPACE_SUMMARY:
      return `${summaryType} - ${simpleDateAtTime(createdAt)}`;

    default:
      return `${jobType}`;
  }
};

/**
 * Checks if the current path matches the return URL for a job
 * Used to determine if we should hide the notification (user is on the loading page)
 */
export const isOnJobLoadingPage = (currentPath: string, context: AIJobNotificationContext): boolean => {
  // Normalize paths by removing trailing slashes
  const normalizedCurrentPath = currentPath.replace(/\/$/, '');
  const normalizedReturnUrl = context.returnUrl.replace(/\/$/, '');

  return normalizedCurrentPath === normalizedReturnUrl;
};

export const updateReviewForAIJob = (job: AIJob, reviewId: string, reviewType: 'authorization' | 'referral') => {
  const queryKeys = reviewType === 'authorization' ? authorizationQueryKeys : referralQueryKeys;
  if (job.isProcessing) return;
  // We can't simply invalidate here because of this edge case:
  //   - User kicks off a summary for Patient A
  //   - User navigates to Patient B and kicks off another summary
  //   - User stays of Patient B's summary tab (loading state)
  //   - Notification for Patient A's completed summary appears, user clicks links
  //     - At this point, Patient A's review query is only marked as stale
  //   - User is taken to Patient A's summary tab, which *briefly displays the empty state*
  // The brief empty state is because the review refetched when it's needed, not before,
  // and the stale, cached review does not have a summary artifact yet
  queryClient.refetchQueries({ queryKey: queryKeys.show({ id: reviewId }) });
};
